import React, { useState, useRef, useEffect } from 'react';
import { format, parse, getYear, getMonth, setYear, setMonth } from 'date-fns';
import calendar_Icon from "../../../images/calendar_icon.svg";
import './datecalendar.css';

/**
 * Custom HecoAnalytics Calendar component
 */

export const DateCalendar = ({ values, onValueChange }) => {
  const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const YEARS = Array.from({ length: 100 }, (_, i) => getYear(new Date()) - 50 + i); // Last 50 and next 50 years

  // Attempt to parse the initial date from a set of formats
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const formats = ['yyyy-MM-dd', 'dd/MM/yyyy', 'MM/dd/yyyy', 'yyyy/MM/dd', 'yyyy/dd/MM', 'dd-MM-yyyy', 'MM-dd-yyyy', 'yyyy-MM-dd', 'yyyy-dd-MM'];
    for (const fmt of formats) {
      const parsedDate = parse(dateStr, fmt, new Date());
      if (!isNaN(parsedDate)) {
        return parsedDate;
      }
    }
    return null; // Null value if the initial date is not correct
  };

  const [showCalendar, setShowCalendar] = useState(false);

  // Format the initial date
  const [selectedDate, setSelectedDate] = useState(() => {
    const parsedDate = parseDate(values);
    return parsedDate ? format(parsedDate, 'dd/MM/yyyy') : '';
  });

  const [displayDate, setDisplayDate] = useState(() => {
    const parsedDate = parseDate(values);
    return parsedDate || new Date(); // Default to current date if parsing fails
  });

  const calendarRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [calendarRef]);

  // Show an empty input if the user clicks on the bin button
  useEffect(() => {
    if (values === "") {
      setSelectedDate("");
      setDisplayDate(new Date());
    } else {
      const parsedDate = parseDate(values);
      setSelectedDate(parsedDate ? format(parsedDate, 'dd/MM/yyyy') : '');
      setDisplayDate(parsedDate || new Date());
    }
  }, [values]);

  const handleDateClick = (day) => {
    const newDate = new Date(getYear(displayDate), getMonth(displayDate), day);
    const formattedDate = format(newDate, 'dd/MM/yyyy');
    setSelectedDate(formattedDate);
    onValueChange(formattedDate);
    setShowCalendar(false);
  };

  const handleMonthChange = (e) => {
    const newMonth = parseInt(e.target.value, 10);
    const newDisplayDate = setMonth(displayDate, newMonth);
    setDisplayDate(newDisplayDate);
  };

  const handleYearChange = (e) => {
    const newYear = parseInt(e.target.value, 10);
    const newDisplayDate = setYear(displayDate, newYear);
    setDisplayDate(newDisplayDate);
  };

  const renderCalendar = () => {
    const days = [];
    const startDay = new Date(getYear(displayDate), getMonth(displayDate), 1).getDay();
    const numDays = new Date(getYear(displayDate), getMonth(displayDate) + 1, 0).getDate();

    for (let i = 0; i < startDay; i++) {
      days.push(<div key={`empty-${i}`} className="day empty" />);
    }

    for (let i = 1; i <= numDays; i++) {
      days.push(
        <div
          key={i}
          className={`day ${selectedDate && parse(selectedDate, 'dd/MM/yyyy', new Date()).getDate() === i && parse(selectedDate, 'dd/MM/yyyy', new Date()).getMonth() === displayDate.getMonth() && parse(selectedDate, 'dd/MM/yyyy', new Date()).getFullYear() === displayDate.getFullYear() ? 'selected' : ''}`}
          onClick={() => handleDateClick(i)}
        >
          {i}
        </div>
      );
    }

    return (
      <div className="calendar" ref={calendarRef}>
        <div className="calendar-header">
          <select value={getMonth(displayDate)} onChange={handleMonthChange}>
            {MONTHS.map((month, index) => (
              <option key={month} value={index}>{month}</option>
            ))}
          </select>
          <select value={getYear(displayDate)} onChange={handleYearChange}>
            {YEARS.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
        <div className="calendar-grid">
          {days}
        </div>
      </div>
    );
  };

  return (
    <div className="date-picker">
      <div className='date-picker-input'>
        <input
          type="text"
          value={selectedDate || ''}
          readOnly
          onClick={() => setShowCalendar(!showCalendar)}
          placeholder="Select a date..."
        />
        <button onClick={() => setShowCalendar(!showCalendar)}>
          <img src={calendar_Icon} alt='calendar'/>
        </button>
      </div>
      {showCalendar && renderCalendar()}
    </div>
  );
};
